import { useState, useEffect } from "react";
import { API_KEY } from "../Constant/TextConstant.js";

export const useUserLocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getLocationDetails = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://api.olamaps.io/places/v1/reverse-geocode?latlng=${lat},${lng}&api_key=${API_KEY.OLA_API_KEY}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.results?.length) {
          const result = data.results[0];
          const components = result.address_components || [];

          let streetName = "";
          let subLocality = "";
          let locality = "";
          let pincode = "";

          components.forEach((component) => {
            const types = component.types || [];
            if (types.includes("street_address")) {
              streetName = component.long_name;
            } else if (
              types.includes("sublocality") ||
              types.includes("administrative_area_level_3")
            ) {
              subLocality = component.long_name;
            } else if (types.includes("locality")) {
              locality = component.long_name;
            } else if (types.includes("postal_code")) {
              pincode = component.long_name;
            }
          });

          const locationDetails = {
            streetName,
            subLocality,
            locality,
            pincode,
            formattedAddress: result.formatted_address,
          };


          return locationDetails;
        }
      }
      return null;
    } catch (err) {
      return null;
    }
  };

  const updateSessionLocation = (locationData) => {
    sessionStorage.setItem(
      "user_session_location",
      JSON.stringify(locationData)
    );
  };

  const clearSessionLocation = () => {
    sessionStorage.removeItem("user_session_location");
  };

  useEffect(() => {
    const initializeLocation = async () => {
      try {
        // Check for existing session location
        const sessionLocation = sessionStorage.getItem("user_session_location");
        if (sessionLocation) {
          const parsed = JSON.parse(sessionLocation);
          setLocation(parsed);
          setIsLoading(false);
          return;
        }

        // Always fetch fresh location when app starts
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              try {
                const { latitude, longitude } = position.coords;
                const details = await getLocationDetails(latitude, longitude);

                const newLocation = {
                  coordinates: { latitude, longitude },
                  address: details?.formattedAddress || "",
                  details: details || null,
                };

                // Save to session storage
                updateSessionLocation(newLocation);
                setLocation(newLocation);
              } catch (err) {
                setError("Couldn't process location details");
                setLocation({
                  coordinates: { latitude: 0, longitude: 0 },
                  address: "",
                  details: null,
                });
              }
              setIsLoading(false);
            },
            (err) => {
              setError("Location access denied");
              setLocation({
                coordinates: { latitude: 0, longitude: 0 },
                address: "",
                details: null,
              });
              setIsLoading(false);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        } else {
          setError("Geolocation not supported");
          setLocation({
            coordinates: { latitude: 0, longitude: 0 },
            address: "",
            details: null,
          });
          setIsLoading(false);
        }
      } catch (err) {
        setError("Error getting location");
        setLocation({
          coordinates: { latitude: 0, longitude: 0 },
          address: "",
          details: null,
        });
        setIsLoading(false);
      }
    };

    // Initialize location
    initializeLocation();

    // Clear session storage when window is closed
    const handleBeforeUnload = () => {
      clearSessionLocation();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const updateLocation = (newLocation) => {
    updateSessionLocation(newLocation);
    setLocation(newLocation);
  };

  return {
    location,
    error,
    isLoading,
    updateLocation,
  };
};
